import { Paper, Stack } from '@mantine/core';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router';
import { SidebarItem, DIVIDER } from 'src/UI/page-nav/sidebar/types';
import { PageHeader } from './components/PageHeader';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { IconFolder } from '@tabler/icons-react';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { FileDragDropProvider } from './context/FileDragDropContext';
import { useFileDragDrop } from './hooks/useFileDragDrop';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { UploadedFile } from '../hooks/file-uploads/types';

const findCurrentLinkCount = (links: SidebarItem[], currentUrl: string): number | undefined => {
  for (const item of links) {
    if (item === DIVIDER) continue;

    if (item.href === currentUrl && item.count !== undefined) {
      return item.count;
    }

    if (item.children) {
      const childCount = findCurrentLinkCount(item.children, currentUrl);
      if (childCount !== undefined) return childCount;
    }
  }
  return undefined;
};

type FileUploadLayoutProps = {
  children: React.ReactNode;
  title: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultCount?: number;
  actions?: React.ReactNode;
};

export default function FileUploadLayout({
  children,
  title,
  showSearch,
  searchValue,
  onSearchChange,
  searchResultCount,
  actions,
}: FileUploadLayoutProps) {
  const { clientId, client } = useCurrentClient();
  const { files, filesToAdd, moveFile, refetchFiles } = useFileUploads();
  const location = useLocation();

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, targetId: parentId },
      );
    }
  };

  const dragDropHook = useFileDragDrop({
    allFiles: files,
    handleMoveFile,
    clientId,
  });

  const folderLinks: SidebarItem = useMemo(() => {
    const FolderIcon = (props: { size?: number; color?: string }) => <IconFolder {...props} />;

    const buildFolderHierarchy = (parentId: string | null = null): SidebarItem[] => {
      return files
        .filter(file => file.isFolder && file.parentId === parentId)
        .map(folder => ({
          label: folder.file.fileName,
          href: `/client/${clientId}/document/files/folder/${folder.id}`,
          icon: FolderIcon,
          children: buildFolderHierarchy(folder.id),
        }));
    };

    const rootFolders = buildFolderHierarchy();

    return {
      label: `All ${client?.firstName || 'client'}'s documents`,
      href: `/client/${clientId}/document/files`,
      icon: FolderIcon,
      children: rootFolders,
      defaultExpanded: true,
    };
  }, [files, clientId, client?.firstName]);

  const sidebarLinks: SidebarItem[] = [
    { label: 'Add documents', count: filesToAdd.length, href: `/client/${clientId}/document/add` },
    DIVIDER,
    {
      label: 'Siesta AI generated',
      href: `/client/${clientId}/document/generated`,
      children: [
        {
          label: 'Suitability reports',
          href: `/client/${clientId}/document/suitability-reports`,
        },
        { label: 'Meeting agendas', href: `/client/${clientId}/document/meeting-agendas` },
        { label: 'Meeting notes', href: `/client/${clientId}/document/meeting-notes` },
        { label: 'Data exports', href: `/client/${clientId}/document/data-exports` },
      ],
    },
    DIVIDER,
    folderLinks,
  ];

  const currentUrl = location.pathname;
  const badgeCount = findCurrentLinkCount(sidebarLinks, currentUrl);

  const content = (
    <Paper variant="content">
      <Stack gap="xxl">
        <PageHeader
          title={title}
          badgeCount={badgeCount}
          showSearch={showSearch}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchResultCount={searchResultCount}
          actions={actions}
        />

        {children}
      </Stack>
    </Paper>
  );

  return (
    <FileDragDropProvider contextValue={dragDropHook}>
      <PageWithSidebar links={sidebarLinks} activeUrl={location.pathname}>
        {content}
      </PageWithSidebar>
    </FileDragDropProvider>
  );
}
